import { useCallback, useEffect, useRef, useState } from "react";
import debounce from "lodash/debounce";
import { useAppStore } from "@/store/app/slice";
import { ContractorsRepository } from "@/api/contractors/repository";
import { ContractorsService } from "@/api/contractors/service";
import type { IContractor } from "@/types/users-contractors";
import { serverErrorHandler } from "@/utils/serverErrorHandler";

export const useFetchContractorProfile = () => {
  const abortController = useRef<AbortController | null>(null);
  const user = useAppStore((s) => s.user);
  const [profile, setProfile] = useState<IContractor | null>(null);

  const fetchProfile = useCallback(() => {
    if (!user) {
      return;
    }

    const contractorRepository = new ContractorsRepository(
      new ContractorsService(),
    );

    abortController.current?.abort();
    abortController.current = new AbortController();
    const signal = abortController.current.signal;

    contractorRepository
      .getProfileByUserId(
        {
          userId: user.id,
        },
        { signal },
      )
      .then((response) => {
        setProfile(response);
      })
      .catch(serverErrorHandler());
  }, [user?.id]);

  useEffect(() => {
    const debouncedFetch = debounce(fetchProfile, 300);
    debouncedFetch();

    return () => {
      debouncedFetch.cancel();
      abortController.current?.abort();
    };
  }, [fetchProfile]);

  return {
    fetchProfile,
    profile,
  };
};
